<template>
  <div class="container-navbar">
    <nav id="menu" class="navbar navbar-expand-lg navbar-dark sticky-top">
      <RouterLink class="navbar-brand py-0" :to="{ name: 'home' }">
        <img src="@/assets/images/logoProsegurCashFullBlanco.svg" alt="PROSEGUR Cash" height="55" />
      </RouterLink>

      <button
        class="navbar-toggler"
        type="button"
        aria-label="Toggle navigation"
        @click="navOpen = !navOpen"
      >
        <span class="navbar-toggler-icon" />
      </button>

      <div id="nav-collapse" class="collapse navbar-collapse" :class="{ show: navOpen }">
        <ul class="navbar-nav">
          <li class="nav-item">
            <RouterLink class="nav-link" :to="{ name: 'home' }">{{ t('navbar.home') }}</RouterLink>
          </li>
          <li v-if="hasAnyRolAdmin" class="nav-item">
            <RouterLink class="nav-link" :to="{ name: 'admin' }">{{
              t('navbar.admin')
            }}</RouterLink>
          </li>
        </ul>

        <ul class="navbar-nav ms-auto">
          <!-- Region dropdown -->
          <li
            v-if="availableRegions.length > 1 && !regionLocked"
            class="nav-item dropdown btn me-lg-1"
          >
            <a
              class="nav-link dropdown-toggle"
              href="#"
              @click.stop.prevent="regionOpen = !regionOpen"
            >
              <i class="pi pi-map-marker me-1" />
              <span>{{ currentRegion }}</span>
            </a>
            <ul class="dropdown-menu dropdown-menu-end" :class="{ show: regionOpen }">
              <li v-for="region in availableRegions" :key="region">
                <a
                  class="dropdown-item"
                  :class="{ active: region === currentRegion }"
                  href="#"
                  @click.prevent="changeRegion(region)"
                  >{{ region }}</a
                >
              </li>
            </ul>
          </li>
          <li v-else class="nav-item dropdown btn me-lg-1">
            <a class="nav-link" href="#">
              <i class="pi pi-map-marker me-1" />
              <span>{{ currentRegion }}</span>
            </a>
          </li>

          <!-- Language dropdown -->
          <li class="nav-item dropdown btn me-lg-1">
            <a class="nav-link dropdown-toggle" href="#" @click.stop.prevent="langOpen = !langOpen">
              <i class="pi pi-globe me-1" />
              <span>{{ t('language.' + currentLang) }}</span>
            </a>
            <ul class="dropdown-menu dropdown-menu-end" :class="{ show: langOpen }">
              <li v-for="(lang, index) in langs" :key="index">
                <a
                  class="dropdown-item"
                  :class="{ active: lang === currentLang }"
                  href="#"
                  @click.prevent="changeLanguage(lang)"
                  >{{ t('language.' + lang) }}</a
                >
              </li>
            </ul>
          </li>

          <!-- User dropdown -->
          <li id="dropdownUser" class="nav-item dropdown btn">
            <a class="nav-link dropdown-toggle" href="#" @click.stop.prevent="userOpen = !userOpen">
              <IconPersonCircle width="1.5em" height="1.5em" />
              <div>
                <div>{{ (userInfo.name || '') + ' ' + (userInfo.surname || '') }}</div>
                <div class="link-prosegur">{{ userInfo.username }}</div>
              </div>
            </a>
            <ul class="dropdown-menu dropdown-menu-end" :class="{ show: userOpen }">
              <li>
                <RouterLink class="dropdown-item" :to="{ name: 'profile' }">
                  <i class="pi pi-user me-2" />
                  {{ t('navbar.profile') }}
                </RouterLink>
              </li>
              <template v-if="!isCorporate && !isFederated">
                <li>
                  <RouterLink
                    class="dropdown-item"
                    :to="{ path: '/callback', query: { type: 'CHANGE_PWD' } }"
                    target="_blank"
                  >
                    <i class="pi pi-key me-2" />
                    {{ t('user.changePassword') }}
                  </RouterLink>
                </li>
                <li>
                  <RouterLink
                    class="dropdown-item"
                    :to="{ path: '/callback', query: { type: 'RESET_MFA' } }"
                    target="_blank"
                  >
                    <Icon2FA width="20" height="20" class="me-2" />
                    {{ t('user.resetMFA') }}
                  </RouterLink>
                </li>
              </template>
              <li><hr class="dropdown-divider" /></li>
              <li>
                <a class="dropdown-item" href="#" @click.prevent="logout">
                  <i class="pi pi-power-off me-2" />
                  {{ t('navbar.logout') }}
                </a>
              </li>
            </ul>
          </li>
        </ul>
      </div>
    </nav>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, onBeforeUnmount } from 'vue'
import { useI18n } from 'vue-i18n'
import Swal from 'sweetalert2'
import { useAuthStore } from '@/stores/auth'
import { useRegionStore } from '@/stores/region'
import { auth } from '@/services'
import { eventBus } from '@/composables/useEventBus'
import Icon2FA from '@/components/Icon2FA.vue'
import IconPersonCircle from '@/components/IconPersonCircle.vue'

const { locale, t } = useI18n()
const authStore = useAuthStore()
const regionStore = useRegionStore()

const isCorporate = computed(() => authStore.isCorporate)
const isFederated = computed(() => authStore.isFederated)
const hasAnyRolAdmin = computed(() => authStore.hasAnyRoleAdmin)
const regionLocked = computed(() => regionStore.regionLocked)
const currentRegion = computed(() => regionStore.region)
const authUser = computed(() => authStore.user)

const navOpen = ref(false)
const regionOpen = ref(false)
const langOpen = ref(false)
const userOpen = ref(false)

const currentLang = ref(locale.value)
const langs = ['de', 'es', 'en', 'pt']

const userInfo = computed(() => {
  if (
    authUser.value?.country?.region &&
    availableRegions.value.length > 0 &&
    availableRegions.value.length < 2
  ) {
    changeRegion(authUser.value.country.region.name)
  }
  return authUser.value ?? {}
})

const availableRegions = computed(() => {
  if (!authUser.value) return []
  if (authUser.value.admin) {
    return [
      ...new Set(
        (regionStore.regions ?? [])
          .filter((r: { name: string }) => r.name !== 'GLOBAL')
          .map((r: { name: string }) => r.name),
      ),
    ]
  }

  const regions = (authUser.value.apps ?? [])
    .map((app: { region: { name: string } }) => app.region.name)
    .filter((region): region is string => region !== 'GLOBAL')

  const userRegion = authUser.value.country?.region?.name
  if (userRegion && !regions.includes(userRegion)) {
    regions.unshift(userRegion)
  }

  return [...new Set(regions)]
})

function changeRegion(newRegion: string) {
  regionStore.changeRegion(newRegion)
  eventBus.fire('region-change', newRegion)
  regionOpen.value = false
}

function changeLanguage(newLanguage: string) {
  document.cookie = `LOCALE=${newLanguage}; path=/`
  currentLang.value = newLanguage
  locale.value = newLanguage
  eventBus.fire('language-change', newLanguage)
  langOpen.value = false
}

function closeDropdowns() {
  regionOpen.value = false
  langOpen.value = false
  userOpen.value = false
}

onMounted(() => document.addEventListener('click', closeDropdowns))
onBeforeUnmount(() => document.removeEventListener('click', closeDropdowns))

async function logout() {
  userOpen.value = false
  const result = await Swal.fire({
    title: t('navbar.logout'),
    text: t('navbar.confirmLogout'),
    icon: 'question',
    showCancelButton: true,
    confirmButtonText: t('button.yes'),
    cancelButtonText: t('button.no'),
  })
  if (result.isConfirmed) {
    auth.signOut()
  }
}
</script>

<style lang="scss" scoped>
@use '../assets/scss/variables' as *;

.container-navbar {
  z-index: 1;
  position: absolute;
  left: 0;
  right: 0;
}

#menu {
  min-height: 65px;
  background-color: rgba(0, 0, 0, 0.6);

  :deep(.navbar-nav .nav-item a.nav-link) {
    color: white;

    &:hover,
    &:active,
    &:focus {
      color: white;
    }

    &.router-link-active {
      color: $prosegur-color;
    }
  }

  :deep(.dropdown-toggle::after) {
    color: $prosegur-color;
  }

  #dropdownUser {
    :deep(.dropdown-toggle::after) {
      display: none;
    }

    > .nav-link {
      display: flex;
      padding: 0.55rem 1.5rem;

      svg {
        margin: auto 1rem auto 0;
        line-height: 2;
      }

      .link-prosegur {
        color: $prosegur-color !important;
      }
    }
  }
}

@media (min-width: 992px) {
  #menu {
    padding: 0 1rem !important;

    .navbar-nav .nav-item {
      position: relative;

      .dropdown-menu {
        top: 100%;
        margin-top: 0;
      }

      &.btn {
        padding: 0;
        border: 0;
      }

      a.nav-link {
        padding: 1.3rem;

        > span {
          padding-left: 4px;
        }
      }
    }

    .navbar-nav:not(.ms-auto) .nav-item a::after {
      background: $prosegur-color;
      bottom: -0.1rem;
      content: '';
      display: none;
      height: 5px;
      left: 0;
      position: absolute;
      width: 100%;
    }

    .navbar-nav:not(.ms-auto) .nav-item:hover a::after {
      display: inherit;
    }

    #dropdownUser {
      display: flex;
      border-left: 1px solid rgba($base-color, 0.5);
      border-radius: 0;

      .dropdown-item {
        min-width: 220px;
        display: flex;
        align-items: center;
        padding: 0.8rem 1.5rem;
      }
    }
  }
}

@media (max-width: 991px) {
  #menu {
    :deep(.navbar-nav:not(.ms-auto) .nav-item),
    :deep(.dropdown:not(:last-child)) {
      border-bottom: 1px solid #6d7073;
    }

    :deep(.navbar-nav .nav-item) {
      padding-left: 16px;
      padding-right: 16px;
    }

    :deep(.navbar-nav .nav-item.btn) {
      text-align: left;
      padding-top: 0;
      padding-bottom: 0;
      border-radius: 0;
    }

    #dropdownUser > .nav-link {
      padding-left: 0;
      padding-right: 0;
    }
  }

  .container-navbar {
    background-image: url($url-background);
    background-repeat: no-repeat;
    background-size: cover;
    background-position: center top;

    #menu {
      background-color: rgba(0, 0, 0, 0.8);
    }
  }
}
</style>
